import { useNavigate } from '@tanstack/react-router';
import { LogOut, Search } from 'lucide-react';
import { useCurrentUser, useLogout } from '../../features/auth/api';
import { useEnumLabels, useT } from '../../i18n';
import { Menu, MenuItem, MenuSeparator } from '../ui/menu';
import { PreferenceMenu } from './PreferenceMenu';

export interface TopbarProps {
  /** Present inside a project workspace, where search is scoped to it. */
  onOpenSearch?: () => void;
}

export const Topbar = ({ onOpenSearch }: TopbarProps) => {
  const { t } = useT();
  const { role } = useEnumLabels();
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout.mutateAsync();
    await navigate({ to: '/login', search: { next: undefined } });
  };

  return (
    <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border bg-surface px-4">
      {onOpenSearch ? (
        <button
          type="button"
          onClick={onOpenSearch}
          className="flex h-8 min-w-56 items-center gap-2 rounded-md border border-border px-2.5 text-[13px] text-ink-subtle transition-colors hover:border-border-strong hover:text-ink-muted"
        >
          <Search className="size-3.5" />
          {t('nav.searchProject')}
          <kbd className="ml-auto rounded border border-border bg-canvas px-1 font-mono text-[11px]">
            ⌘K
          </kbd>
        </button>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-1">
        <PreferenceMenu />

        <Menu
          trigger={
            <button
              type="button"
              className="flex items-center gap-2 rounded-md px-2 py-1 text-[13px] transition-colors hover:bg-sunken"
            >
              <span className="grid size-6 place-items-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent-fg">
                {(user?.name ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="hidden text-ink sm:inline">{user?.name}</span>
            </button>
          }
        >
          <div className="px-2 py-1.5">
            <p className="text-[13px] font-medium text-ink">{user?.name}</p>
            <p className="text-[12px] text-ink-subtle">
              {user ? `${user.login} · ${role(user.role)}` : ''}
            </p>
          </div>
          <MenuSeparator />
          <MenuItem onSelect={() => void signOut()}>
            <LogOut className="size-3.5" />
            {t('nav.signOut')}
          </MenuItem>
        </Menu>
      </div>
    </header>
  );
};
